import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Gamepad2, Layers, Code, Laptop, Users, ChevronRight, PenTool } from 'lucide-react';

const GameDevelopment: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Interactive Arts Institute";
    
    return (
        <div className="min-h-screen bg-[#111111] font-sans text-slate-200 selection:bg-yellow-500 selection:text-black">
            {/* Navbar */}
            <nav className="fixed w-full z-50 bg-[#111]/90 backdrop-blur border-b border-white/5">
                <div className="max-w-7xl mx-auto px-8 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="bg-yellow-500 p-2 rounded-lg text-black">
                            <Gamepad2 size={24} strokeWidth={2.5} />
                        </div>
                        <span className="font-bold text-xl tracking-tight text-white uppercase">{schoolName}</span>
                    </div>
                    
                    <div className="hidden md:flex items-center gap-10 text-sm font-medium text-slate-400 uppercase tracking-widest">
                        <a href="#" className="hover:text-yellow-500 transition-colors">Academics</a>
                        <a href="#" className="hover:text-yellow-500 transition-colors">Showcase</a>
                        <a href="#" className="hover:text-yellow-500 transition-colors">Labs</a>
                        <a href="#" className="hover:text-yellow-500 transition-colors">Faculty</a>
                    </div>
                    
                    <div className="flex gap-4">
                        <Button className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold uppercase tracking-wide px-6">
                            Apply Now
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="pt-32 pb-24 px-8 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div className="relative z-10">
                        <div className="inline-block border-l-4 border-yellow-500 pl-4 mb-8">
                            <span className="text-yellow-500 font-bold uppercase tracking-widest text-sm block mb-1">Bachelor of Science</span>
                            <span className="text-white text-lg font-medium">Game Design & Development</span>
                        </div>
                        
                        <h1 className="text-5xl md:text-7xl font-bold text-white leading-none mb-8">
                            Design the <br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600">Infinite.</span>
                        </h1>
                        
                        <p className="text-xl text-slate-400 mb-10 max-w-lg leading-relaxed font-light">
                            Where art meets code. Join the next generation of game creators in an immersive, studio-based learning environment.
                        </p>
                        
                        <div className="flex flex-wrap gap-4">
                            <Button className="h-14 px-8 bg-white text-black hover:bg-slate-200 font-bold text-sm uppercase tracking-widest rounded-none">
                                View Curriculum
                            </Button>
                            <Button variant="outline" className="h-14 px-8 border-white/20 text-white hover:bg-white/5 font-bold text-sm uppercase tracking-widest rounded-none">
                                Student Projects
                            </Button>
                        </div>
                    </div>

                    {/* Hero Image / Collage */}
                    <div className="relative h-[500px] w-full bg-[#1a1a1a] p-2 overflow-hidden border border-white/5">
                         <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1556438050-bf886e572833?q=80&w=1974&auto=format&fit=crop')] bg-cover bg-center opacity-40"></div>
                         <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent"></div>
                         
                         <div className="absolute bottom-8 left-8 right-8">
                             <div className="flex items-center justify-between border-t border-white/20 pt-6">
                                 <div>
                                     <h3 className="text-white font-bold text-lg mb-1">Project: "Neon Horizon"</h3>
                                     <p className="text-yellow-500 text-xs uppercase tracking-widest">Senior Capstone 2025</p>
                                 </div>
                                 <div className="w-12 h-12 border border-white/30 rounded-full flex items-center justify-center hover:bg-white hover:text-black transition-colors cursor-pointer">
                                     <ChevronRight size={24} />
                                 </div>
                             </div>
                         </div>
                    </div>
                </div>
            </header>

            {/* Statistics / Quick Facts */}
            <section className="border-y border-white/5 bg-[#161616] py-16">
                <div className="max-w-7xl mx-auto px-8 grid grid-cols-2 md:grid-cols-4 gap-12 text-center md:text-left">
                    <div>
                        <div className="text-4xl font-bold text-white mb-2">#6</div>
                        <div className="text-xs text-slate-500 uppercase tracking-widest">Ranked Game School</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-white mb-2">92%</div>
                        <div className="text-xs text-slate-500 uppercase tracking-widest">Hired within 6mo</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-white mb-2">24/7</div>
                        <div className="text-xs text-slate-500 uppercase tracking-widest">Lab Access</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-white mb-2">15:1</div>
                        <div className="text-xs text-slate-500 uppercase tracking-widest">Student-Faculty Ratio</div>
                    </div>
                </div>
            </section>

            {/* Areas of Focus */}
            <section className="py-24 px-8 max-w-7xl mx-auto">
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-white mb-6">Disciplines</h2>
                    <div className="w-20 h-1 bg-yellow-500"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Code, title: "Game Programming", desc: "Engine architecture, AI, and gameplay systems in C++ and C#." },
                        { icon: PenTool, title: "Art & Animation", desc: "3D modeling, texturing, rigging, and environmental design." },
                        { icon: Layers, title: "Level Design", desc: "Crafting immersive experiences, pacing, and player progression." }
                    ].map((item, i) => (
                        <div key={i} className="group p-8 border border-white/10 hover:bg-[#1a1a1a] transition-colors cursor-pointer relative overflow-hidden">
                            <item.icon size={32} className="text-slate-500 mb-6 group-hover:text-yellow-500 transition-colors" />
                            <h3 className="text-xl font-bold text-white mb-4">{item.title}</h3>
                            <p className="text-slate-400 leading-relaxed text-sm mb-8">{item.desc}</p>
                            <span className="text-yellow-500 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                                View Courses <div className="w-8 h-px bg-yellow-500"></div>
                            </span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#050505] py-20 px-8 border-t border-white/5">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-12">
                    <div>
                         <div className="flex items-center gap-2 mb-6">
                            <Gamepad2 size={20} className="text-white" />
                            <span className="font-bold text-white tracking-widest uppercase">{schoolName}</span>
                        </div>
                        <p className="text-slate-500 text-sm max-w-xs">
                            Dedicated to the art and science of interactive entertainment.
                        </p>
                    </div>
                    <div className="flex gap-16 text-sm text-slate-500 uppercase tracking-widest">
                        <ul className="space-y-4">
                            <li><a href="#" className="hover:text-white">Admissions</a></li>
                             <li><a href="#" className="hover:text-white">Campus</a></li>
                              <li><a href="#" className="hover:text-white">Scholarships</a></li>
                        </ul>
                        <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white">Contact</a></li>
                              <li><a href="#" className="hover:text-white">Press</a></li>
                               <li><a href="#" className="hover:text-white">Careers</a></li>
                        </ul>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default GameDevelopment;
